// Coverage card: the per-row `coverage` object and the card `summary` the
// backend stores (vob.services.coverage), and the pure helpers that turn
// their CODES into words, a badge, a cost label and a total. The backend
// sends data; every sentence lives here. Advisory only — nothing changes
// what gets placed.

export type CoverageState = "covered" | "prior_auth" | "not_covered" | "unknown";

export interface RowCoverage {
  state: CoverageState;
  // Why the row is in its state (a code).
  reason?: string;
  cpt?: string;
  code_source?: "catalog" | "catalog_label" | "fee_schedule_name";
  category?: string;
  // The payer's own free-text remarks for the row's benefit category, verbatim.
  remarks?: string[];
  billed?: number;
  allowed?: number;
  plan_pays?: number;
  patient_pays?: number;
  cost_basis?: "copay" | "deductible" | "coinsurance" | "not_covered";
  coinsurance_pct?: number | null;
  deductible_applies?: boolean;
  price_status?: "not_on_fee_schedule" | "benefit_not_itemised";
  rate_source?: "fee_schedule_allowed" | "fee_schedule_charge";
  schedule_item?: string;
  confidence?: "high" | "medium";
  checked_at?: string;
}

export interface CoveragePolicy {
  payer_name: string;
  tier: string;
  status: string;
  relationship?: string;
  end_date?: string | null;
  member_id_last4?: string | null;
}

export interface CoverageFeeSchedule {
  name: string;
  description?: string;
  truncated?: boolean;
  rates?: number;
}

export interface CoverageSummary {
  state: "verified" | "unverified" | "inactive" | "self_pay" | "no_insurance";
  // Why the state is what it is (a code), when not plainly verified.
  reason?: string;
  payer_name?: string;
  plan_family?: string;
  plan_type?: string | null;
  plan_status?: string | null;
  plan_end?: string | null;
  plan_remarks?: string[];
  office_copay?: number | null;
  ehr_eligibility?: string | null;
  ehr_status?: string | null;
  ehr_end_date?: string | null;
  secondary?: string[];
  relationship?: string;
  insured_name?: string | null;
  policies?: CoveragePolicy[];
  deductible?: { total: number; met: number } | null;
  oop?: { total: number; met: number } | null;
  source?: string | null;
  verified_at?: string | null;
  fallback_reason?: string | null;
  requester?: string | null;
  payer_id?: string | null;
  fee_schedule?: CoverageFeeSchedule | null;
  checked_at?: string;
}

export const COVERAGE_BADGES: Record<CoverageState, { label: string; color: string; bg: string; border: string }> = {
  covered: { label: "Covered", color: "#166534", bg: "#DCFCE7", border: "#BBF7D0" },
  prior_auth: { label: "Prior auth", color: "#92400E", bg: "#FEF3C7", border: "#FDE68A" },
  not_covered: { label: "Not covered", color: "#991B1B", bg: "#FEE2E2", border: "#FECACA" },
  unknown: { label: "Unknown", color: "#4B5563", bg: "#F3F4F6", border: "#E5E7EB" },
};

export const CATEGORY_LABELS: Record<string, string> = {
  lab: "Lab benefit",
  di: "Imaging benefit",
  allergy: "Allergy benefit",
  pulmonary: "Pulmonary benefit",
  procedure: "Procedure benefit",
  office: "Office visit benefit",
};

// Why a plan is not plainly verified, by the backend's reason code.
export const SUMMARY_REASONS: Record<string, string> = {
  stedi_not_configured: "Eligibility checks are not configured for this practice.",
  payer_unmapped: "This payer is not mapped for eligibility checks yet; add it to the payer directory.",
  missing_subscriber_identity: "The chart lacks the subscriber's name, member id or date of birth.",
  missing_practice_npi: "The practice has no clinician NPI on file to send an eligibility check under.",
  no_payer_answer: "The payer did not answer the eligibility check.",
  payer_inactive: "The payer reports this plan as inactive.",
  ehr_terminated: "The policy on file is labelled terminated in the EHR.",
  ehr_expired: "The policy on file has ended per the EHR.",
  ehr_inactive: "The policy on file is marked inactive in the EHR.",
  no_ehr_insurance: "No insurance on file from the EHR.",
  no_active_policy: "No active insurance on file.",
  self_pay: "Patient is self-pay.",
};

export const summaryReason = (s: CoverageSummary | null | undefined): string => {
  if (!s?.reason) return "";
  return SUMMARY_REASONS[s.reason] ?? s.reason;
};

const money = (v: number): string => `$${Math.round(v).toLocaleString("en-US")}`;

// One line under a row: what the state means and how the figure was built.
export const rowDetail = (c: RowCoverage | undefined, payer?: string | null): string => {
  if (!c) return "";
  if (c.state === "unknown") {
    if (c.reason === "no_code") return "Couldn't map this order to a billing code.";
    return SUMMARY_REASONS[c.reason ?? ""] ?? "Coverage not verified.";
  }
  const bits: string[] = [];
  if (c.state === "prior_auth") bits.push(`${payer || "The payer"} requires prior authorization`);
  else if (c.state === "not_covered") bits.push("Not covered by this plan");
  else if (c.category) bits.push(CATEGORY_LABELS[c.category] ?? "Benefit");
  if (c.cost_basis === "copay") bits.push(c.patient_pays ? `${money(c.patient_pays)} copay` : "no copay");
  else if (c.cost_basis === "deductible") {
    // No percentage: the payer itemised nothing, so only the deductible is known.
    bits.push(c.coinsurance_pct == null ? "counts toward the deductible" : `deductible, then ${c.coinsurance_pct}%`);
  } else if (c.cost_basis === "coinsurance") bits.push(`${c.coinsurance_pct ?? 0}% coinsurance`);
  else if (c.cost_basis === "not_covered") bits.push("full charge");
  if (c.price_status === "not_on_fee_schedule") bits.push("not on the fee schedule");
  if (c.price_status === "benefit_not_itemised") bits.push("benefit not itemised by the payer");
  return bits.join(" · ");
};

// "$35" (a copay or a fee-schedule allowed amount: exact) or "~$44" (figured
// from the charge alone), or "" when there is no figure.
export const costLabel = (c: RowCoverage | undefined): string => {
  if (!c || c.patient_pays == null) return "";
  if (c.cost_basis === "copay" || c.confidence === "high") return money(c.patient_pays);
  return `~${money(c.patient_pays)}`;
};

// When the patient's share is unknown, the practice's own prices still are:
// "$120 billed" and, when it differs, "allowed $60". Null when there is a
// patient figure (costLabel covers it) or no fee at all.
export const feeOnlyLabel = (c: RowCoverage | undefined): { billed: string; allowed?: string } | null => {
  if (!c || c.billed == null || c.patient_pays != null) return null;
  const out: { billed: string; allowed?: string } = { billed: `${money(c.billed)} billed` };
  if (c.allowed != null && Math.round(c.allowed) !== Math.round(c.billed)) out.allowed = `allowed ${money(c.allowed)}`;
  return out;
};

export const selectedCostTotal = (
  rows: ReadonlyArray<{ index: number; coverage?: RowCoverage | null }>,
  selected: ReadonlySet<number> | null | undefined,
): { billed: number; patient_pays: number; exact: boolean } | null => {
  let billed = 0;
  let patient = 0;
  let seen = false;
  let exact = true;
  for (const r of rows) {
    const c = r.coverage;
    if ((selected && !selected.has(r.index)) || !c || c.patient_pays == null) continue;
    patient += c.patient_pays;
    billed += c.billed ?? 0;
    seen = true;
    if (!(c.cost_basis === "copay" || c.confidence === "high")) exact = false;
  }
  return seen ? { billed, patient_pays: patient, exact } : null;
};

export const totalLabel = (t: { patient_pays: number; exact?: boolean } | null): string => {
  if (!t) return "";
  return t.exact ? money(t.patient_pays) : `~${money(t.patient_pays)}`;
};

const RELATIONSHIP_WORD: Record<string, string> = {
  spouse: "spouse",
  child: "child",
  parent: "parent",
  partner: "partner",
  other: "dependent",
  injured: "injured party",
};

// The header strip: payer, plan, status, deductible progress.
export const summaryParts = (s: CoverageSummary | null | undefined): string[] => {
  if (!s) return [];
  if (s.state === "self_pay") return ["Self-pay patient", "no coverage lookup"];
  if (s.state === "no_insurance") return ["No insurance on file"];
  const parts: string[] = [];
  const payer = [s.payer_name, s.plan_type].filter(Boolean).join(" · ");
  if (payer) parts.push(payer);
  if (s.state === "inactive") {
    parts.push(
      s.ehr_status === "terminated" || s.ehr_status === "expired"
        ? `${s.ehr_status} in the EHR`
        : s.plan_status === "inactive"
          ? "payer reports inactive"
          : "marked inactive in the EHR",
    );
  }
  if (s.relationship && s.relationship !== "self") {
    parts.push(`covered as ${RELATIONSHIP_WORD[s.relationship] ?? "dependent"} of subscriber`);
  }
  if (s.deductible && s.deductible.total > 0) {
    parts.push(`deductible ${money(s.deductible.met)} of ${money(s.deductible.total)} met`);
  }
  if (s.oop && s.oop.total > 0) parts.push(`out-of-pocket ${money(s.oop.met)} of ${money(s.oop.total)}`);
  if (s.office_copay != null && s.office_copay > 0) parts.push(`office copay ${money(s.office_copay)}`);
  if (s.secondary && s.secondary.length) parts.push(`secondary: ${s.secondary.join(", ")}`);
  return parts;
};

const whenLabel = (iso?: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? `today ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

// Where the figures came from, for the small trailing caption.
export const summaryCaption = (s: CoverageSummary | null | undefined): string => {
  if (!s || s.state === "self_pay" || s.state === "no_insurance") return "";
  const bits: string[] = [];
  if (s.ehr_eligibility) bits.push(`EHR eligibility: ${s.ehr_eligibility.toLowerCase()}`);
  const when = whenLabel(s.verified_at);
  switch (s.source) {
    case "stedi_271":
      bits.push(when ? `verified with payer ${when}` : "verified with payer");
      break;
    case "stedi_271_cached":
      bits.push(when ? `payer verified ${when}` : "payer verified (cached)");
      break;
    case "stedi_271_stale":
      bits.push(when ? `last payer answer ${when} · live check failed` : "last payer answer · live check failed");
      break;
    default:
      break;
  }
  if (s.fee_schedule?.name) {
    bits.push(`fees from ${s.fee_schedule.name}${s.fee_schedule.truncated ? " (partial pull)" : ""}`);
  } else {
    bits.push("no fee schedule loaded");
  }
  bits.push("estimates only");
  return bits.join(" · ");
};
